/**
 * OpenTelemetry Logs pipeline for the Observ RUM SDK (story 2.3).
 *
 * Semantic events (`observ.session.*`) are emitted as OTel **log records** and
 * exported over OTLP/HTTP to `{endpoint}/v1/logs` (received by the backend since
 * story 1.4; the session-assembly writer taps them in 1.6). This is the mirror
 * of the trace pipeline in `otel-rum.ts`, on a dedicated, separate provider —
 * the light "logs" channel, never the heavy `/v1/replay` channel.
 *
 * `session.id` is stamped once, at emit time, by {@link emitSessionEvent} — the
 * single point through which every semantic event flows (so a separate
 * `LogRecordProcessor` would be redundant; this also keeps the SDK's
 * sdk-logs API surface minimal).
 */
import type { Logger } from '@opentelemetry/api-logs'
import { OTLPLogExporter } from '@opentelemetry/exporter-logs-otlp-http'
import { BatchLogRecordProcessor, LoggerProvider } from '@opentelemetry/sdk-logs'

import { buildResource } from './resource.js'
import { SESSION_ID_ATTRIBUTE } from './constants.js'
import { getSessionId } from './session.js'
import { userAttributes } from './user.js'
import type { ObservInitOptions } from './types.js'

/**
 * Join an endpoint base URL with the OTLP/HTTP logs path. Mirror of
 * {@link import('./otel-rum.js').buildTracesUrl}: preserves a path prefix, never
 * doubles a trailing slash, drops query/fragment; relative/empty → same-origin.
 */
export function buildLogsUrl(endpoint: string): string {
  try {
    const url = new URL(endpoint)
    url.pathname = `${url.pathname.replace(/\/+$/, '')}/v1/logs`
    url.search = ''
    url.hash = ''
    return url.toString()
  } catch {
    return `${endpoint.replace(/\/+$/, '')}/v1/logs`
  }
}

let provider: LoggerProvider | null = null
let activeLogger: Logger | null = null

/**
 * Wire up the OTel Logs provider and return a `Logger`. Idempotent: a second
 * call returns the already-active logger (first init wins, mirrors
 * `setupOtelRum`). The `x-observ-key` header is omitted when the key is empty
 * (the trace setup already warns about a missing key).
 */
export function setupOtelLogs(options: ObservInitOptions): Logger {
  if (activeLogger) return activeLogger

  const headers: Record<string, string> = {}
  if (options.key) headers['x-observ-key'] = options.key

  const exporter = new OTLPLogExporter({ url: buildLogsUrl(options.endpoint), headers })
  const p = new LoggerProvider({
    resource: buildResource(options),
    processors: [new BatchLogRecordProcessor({ exporter })],
  })

  try {
    const logger = p.getLogger('@observtech/rum')
    // Commit the module state ONLY once everything succeeded — otherwise a
    // partial init would leak the provider's flush timer and latch the guard.
    provider = p
    activeLogger = logger
    return logger
  } catch (err) {
    // Roll back a partial init (mirrors otel-rum.ts): shut the provider down so
    // its BatchLogRecordProcessor timer can't outlive the failure, and leave the
    // guards null so a later setup can retry.
    void p.shutdown()
    throw err
  }
}

/** Inactivity refresh throttle for the cached session id (story 1.2 — avoid a
 *  synchronous sessionStorage write on every event). */
const SESSION_ID_REFRESH_MS = 1_000
let cachedSessionId = ''
let cachedSessionIdAt = 0

/**
 * Resolve the visit `session.id`, refreshing it (and the session's inactivity
 * window) at most once per second. A click burst therefore costs ≤ 1
 * `getSessionId` (→ ≤ 1 sessionStorage write) per second instead of one per
 * event, while still counting interaction as session activity.
 */
function currentSessionId(): string {
  const now = Date.now()
  if (!cachedSessionId || now - cachedSessionIdAt >= SESSION_ID_REFRESH_MS) {
    cachedSessionId = getSessionId()
    cachedSessionIdAt = now
  }
  return cachedSessionId
}

/**
 * Drop the throttled `session.id` cache so the next {@link emitSessionEvent}
 * re-reads it. Called by the session lifecycle the instant a rotation mints a new
 * id, so events emitted within the throttle window after a rotation carry the new
 * id rather than the stale cached one.
 */
export function resetSessionIdCache(): void {
  cachedSessionId = ''
  cachedSessionIdAt = 0
}

/**
 * Emit one semantic event as an OTel log record: `event.name` in the
 * `observ.session.*` namespace + the exact `session.id` correlation attribute
 * (throttled refresh, so an event read also counts as session activity, 1.2) +
 * the end-user identity (`enduser.pseudo.id` / `enduser.id`) + the caller's
 * attributes (which win on key collisions, e.g. lifecycle events passing an
 * explicit `session.id`). Best-effort: never throws to the host page.
 */
export function emitSessionEvent(
  logger: Logger,
  eventName: string,
  attrs: Record<string, string> = {},
): void {
  try {
    logger.emit({
      attributes: {
        'event.name': eventName,
        [SESSION_ID_ATTRIBUTE]: currentSessionId(),
        ...userAttributes(),
        ...attrs,
      },
    })
  } catch (err) {
    console.warn('[observ] semantic event emit failed', err)
  }
}

/**
 * Emit a free-form log record (severity + body), as opposed to a named semantic
 * event. Used by the `console.*` forwarder: it carries the visit `session.id` +
 * end-user identity but NO `event.name`. Critically, its failure path swallows
 * silently — it must NEVER call `console.*` (the caller may have patched console,
 * which would recurse).
 */
export function emitLogRecord(
  logger: Logger,
  severityNumber: number,
  severityText: string,
  body: string,
): void {
  try {
    logger.emit({
      severityNumber,
      severityText,
      body,
      attributes: {
        [SESSION_ID_ATTRIBUTE]: currentSessionId(),
        ...userAttributes(),
      },
    })
  } catch {
    /* swallow silently — never console.* here (the forwarder patched console) */
  }
}

/**
 * @internal Flush + shut the logs provider down and reset the idempotence guard
 * so a later setup can re-init. Called by the SDK teardown.
 */
export async function shutdownOtelLogs(): Promise<void> {
  const p = provider
  provider = null
  activeLogger = null
  await p?.shutdown()
}

/** @internal Test-only: the active logger, or `null` when logs are not set up. */
export function getActiveLogger(): Logger | null {
  return activeLogger
}
